import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import ErrorNotificationComponent from "../../../components/generic/error/ErrorNotificationComponent";
import CustomScrollbar from "../../../components/generic/scrollbar/CustomScrollbar";
import {
  auditlogDateRangeRecoil,
  showAuditLogsDateResetButton,
} from "../../../components/recoil/auditLogDateRangeRecoil";
import IntentExecutor from "../../../intentexecutor/IntentExecutor";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import { performPostRequest } from "../../../service/network/NetworkService";
import { getApiUrl } from "../../../service/network/UrlMapper";
import {
  convertDateToEODTimestamp,
  convertDateToSODTimestamp,
} from "../../../utils/dateutils";
import BackButton from "../../generic/elements/backbutton/BackButton";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import SearchInput from "../../generic/elements/search/SearchInput";
import TableList from "../../generic/elements/tablelist/TableList";
import ConfigurationConstants from "../../utils/configurationconstants";
import DateRangePickerComponent from "../dashboard/components/Shared/DateRangePickerComponent";
import SelectOrganization from "./SelectOrganization";
import SelectTypeOfEvents from "./SelectTypeOfEvents";
import {
  buildWorkforceNameMap,
  descriptionString,
  getUnnamedVolunteerIds,
  LOGIN_FAILED_EVENT,
  LOGIN_FAILED_EVENT_TITLE,
} from "./utils";
import "./auditlog.css";

const PAGE_SIZE = 50;
const EVENT_DESCRIPTION_CONFIGURATION =
  "niramaya.auditlog.event.type.description";

const getConnections = (search, organizationIdentifier) => {
  const connections = Object.fromEntries(new URLSearchParams(search));

  if (organizationIdentifier) {
    connections.organizationIdentifier = organizationIdentifier;
  }

  return connections;
};

const getEventOptions = (eventTitles = {}) =>
  Object.entries(eventTitles).map(([value, label]) => ({ label, value }));

const executeAuditLogSearch = (payload) => {
  const intentExecutor = new IntentExecutor();
  return intentExecutor.executeApiCall(getApiUrl().get_audit_logs, payload);
};

const fetchWorkforceByIdentifiers = async (
  userIdentifiers,
  organizationIdentifier,
  secretKey,
) => {
  const payload = {
    intent: IntentType.GET_WORKFORCE_DETAILS_MAP_BY_USER_IDENTIFIER,
    payload: {
      userIdentifiers,
      ...(secretKey && { secretKey }),
    },
    context: { organizationIdentifier },
  };
  const response = await performPostRequest(
    getApiUrl()[IntentType.GET_WORKFORCE_DETAILS_MAP_BY_USER_IDENTIFIER],
    JSON.stringify(payload),
  );
  return response.json();
};

const isUnavailableUserName = (userName) => {
  const name = userName?.trim();
  if (!name) return true;
  return name.split(/\s+/).every((part) => part === "undefined");
};

const getAuditLogName = (record) => {
  if (!isUnavailableUserName(record?.userName)) {
    return record.userName;
  }
  return record?.payload?.currentValues?.email || "";
};

const AuditLogs = ({
  includeOrganizationIdentifier = true,
  title = "Audit Logs",
}) => {
  const location = useLocation();
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [configDataLoading, setConfigDataLoading] = useState(false);
  const [dateRange, setDateRange] = useRecoilState(auditlogDateRangeRecoil);
  const [, setIsDefaultDateRange] = useState(false);
  const [showResetBtn, setShowResetBtn] = useRecoilState(
    showAuditLogsDateResetButton
  );
  const [hasMoreData, setHasMoreData] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [eventTypeData, setEventTypeData] = useState({});
  const [typesOfEventsSelected, setTypesOfEventsSelected] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState();
  const [workforceNames, setWorkforceNames] = useState({});
  const requestedWorkforceIds = useRef(new Set());
  const configuration = useRecoilValue(currentConfiguration);
  const organizationIdentifier = useGetOrgIdentifier();
  const secretKey =
    configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY]?.key;
  const { startDate, endDate } = dateRange[0];

  const showSearchField =
    configuration[ConfigurationConstants.NIRAMAYA_AUDITLOG_SEARCHFIELD]
      ?.visibility;

  const optionsList = useMemo(() => {
    const eventTitles = eventTypeData?.eventTitle;
    if (!eventTitles || !Object.keys(eventTitles).length) return [];

    return getEventOptions({
      ...eventTitles,
      [LOGIN_FAILED_EVENT]:
        eventTitles[LOGIN_FAILED_EVENT] || LOGIN_FAILED_EVENT_TITLE,
    });
  }, [eventTypeData]);

  const connections = useMemo(
    () =>
      getConnections(
        location.search,
        includeOrganizationIdentifier
          ? organizationIdentifier
          : selectedOrganization,
      ),
    [
      includeOrganizationIdentifier,
      location.search,
      organizationIdentifier,
      selectedOrganization,
    ],
  );

  const selectedEventTypes = useMemo(
    () =>
      typesOfEventsSelected.length
        ? typesOfEventsSelected.map(({ value }) => value)
        : optionsList.map(({ value }) => value),
    [optionsList, typesOfEventsSelected],
  );

  const auditLogRequest = useMemo(
    () => ({
      connections,
      from: convertDateToSODTimestamp(startDate),
      to: convertDateToEODTimestamp(endDate),
      types: selectedEventTypes,
      pageSize: PAGE_SIZE,
    }),
    [connections, endDate, selectedEventTypes, startDate],
  );

  const canFetchAuditLogs =
    optionsList.length > 0 || typesOfEventsSelected.length > 0;

  const columns = useMemo(
    () => [
      {
        title: "Date & Time",
        dataIndex: "originTimestamp",
        key: "originTimestamp",
        sorter: {
          compare: (a, b) => a.originTimestamp - b.originTimestamp,
        },
        render: (value) => new Date(value).toLocaleString("en-US"),
      },
      {
        title: "Name",
        dataIndex: "userName",
        sorter: {
          compare: (a, b) => getAuditLogName(a).localeCompare(getAuditLogName(b)),
        },
        key: "userName",
        render: (_, record) => getAuditLogName(record),
      },
      {
        title: "Event Type",
        dataIndex: "type",
        key: "type",
        render: (value) =>
          eventTypeData?.eventTitle?.[value] ||
          (value === LOGIN_FAILED_EVENT ? LOGIN_FAILED_EVENT_TITLE : undefined),
      },
      {
        title: "Description",
        dataIndex: "type",
        key: "type",
        width: "35%",
        render: (value, data) =>
          descriptionString(
            eventTypeData?.eventDescription?.[value],
            data,
            workforceNames,
          ),
      },
    ],
    [eventTypeData, workforceNames],
  );

  useEffect(() => {
    const missingIds = getUnnamedVolunteerIds(auditLogs).filter(
      (id) => !requestedWorkforceIds.current.has(id),
    );
    if (!missingIds.length) return;

    missingIds.forEach((id) => requestedWorkforceIds.current.add(id));

    const resolveWorkforceNames = async () => {
      try {
        const workforceById = await fetchWorkforceByIdentifiers(
          missingIds,
          connections.organizationIdentifier || organizationIdentifier,
          secretKey,
        );
        const names = buildWorkforceNameMap(workforceById);
        if (Object.keys(names).length) {
          setWorkforceNames((prev) => ({ ...prev, ...names }));
        }
      } catch {
        missingIds.forEach((id) => requestedWorkforceIds.current.delete(id));
      }
    };

    resolveWorkforceNames();
  }, [
    auditLogs,
    connections.organizationIdentifier,
    organizationIdentifier,
    secretKey,
  ]);

  const fetchMoreData = useCallback(async () => {
    if (!hasMoreData) return;

    try {
      const response = await executeAuditLogSearch({
        ...auditLogRequest,
        pageNumber,
      });
      const newResults = response?.results || [];

      if (newResults.length) {
        setAuditLogs((prevData) => [...prevData, ...newResults]);
        setPageNumber((prevPageNumber) => prevPageNumber + 1);
        setHasMoreData(newResults.length === PAGE_SIZE);
      } else {
        setHasMoreData(false);
      }
    } catch (error) {
      ErrorNotificationComponent(error);
      setHasMoreData(false);
    }
  }, [auditLogRequest, hasMoreData, pageNumber]);

  useEffect(() => {
    const getEventDescriptionData = async () => {
      setConfigDataLoading(true);
      const eventDescriptionPayload = {
        intent: "get_configuration",
        payload: {
          configurationKey: [EVENT_DESCRIPTION_CONFIGURATION],
        },
        context: {},
      };

      try {
        const response = await performPostRequest(
          getApiUrl()[IntentType.GET_CONFIGURATION],
          JSON.stringify(eventDescriptionPayload),
        );
        const result = await response.json();
        const configurationValue = result?.[0]?.configurationValue;

        if (configurationValue) {
          const eventData = JSON.parse(configurationValue);
          setEventTypeData(eventData);
        }
      } catch (error) {
        ErrorNotificationComponent(error);
      } finally {
        setConfigDataLoading(false);
      }
    };

    getEventDescriptionData();
  }, []);

  useEffect(() => {
    const getAuditLogs = async () => {
      setAuditLogsLoading(true);

      try {
        const response = await executeAuditLogSearch({
          ...auditLogRequest,
          pageNumber: 0,
        });
        const results = response?.results;

        if (results) {
          setAuditLogs(results);
        }

        if (results?.length < PAGE_SIZE) {
          setHasMoreData(false);
        } else {
          setPageNumber(1);
          setHasMoreData(true);
        }
      } catch (error) {
        ErrorNotificationComponent(error);
      } finally {
        setAuditLogsLoading(false);
      }
    };

    if (canFetchAuditLogs) {
      getAuditLogs();
    }
  }, [auditLogRequest, canFetchAuditLogs]);

  return (
    <section className="section-content">
      <div className="form-renderer-back-button">
        <BackButton />
        <p>{title}</p>
      </div>
      <div
        className={
          includeOrganizationIdentifier
            ? "audit-log-actions-bar"
            : "audit-log-generic-layout"
        }
      >
        <SelectTypeOfEvents
          typesOfEventsSelected={typesOfEventsSelected}
          setTypesOfEventsSelected={setTypesOfEventsSelected}
          optionsList={optionsList}
        />
        {includeOrganizationIdentifier ? (
          <div className="row">
            <div
              className="col d-flex justify-content-end align-items-center mb-3 dateSection"
              style={{ columnGap: "1rem" }}
            >
              <DateRangePickerComponent
                setIsDefaultDateRange={setIsDefaultDateRange}
                setDateRange={setDateRange}
                dateRange={dateRange}
                showResetBtn={showResetBtn}
                setShowResetBtn={setShowResetBtn}
              />
            </div>
          </div>
        ) : (
          <div className="audit-log-date-range">
            <DateRangePickerComponent
              setIsDefaultDateRange={setIsDefaultDateRange}
              setDateRange={setDateRange}
              dateRange={dateRange}
              showResetBtn={showResetBtn}
              setShowResetBtn={setShowResetBtn}
            />
          </div>
        )}
        {!includeOrganizationIdentifier && (
          <SelectOrganization
            selectedOrganization={selectedOrganization}
            setSelectedOrganization={setSelectedOrganization}
          />
        )}
      </div>
      {showSearchField && (
        <div className="audit-log-search-wrapper">
          <div className="event-type-label"></div>
          <SearchInput
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            cssClass="searchFilterContainer"
            searchPlaceholder="Search User Name..."
          />
        </div>
      )}

      <CustomScrollbar>
        <TableList
          data={auditLogs}
          columns={columns}
          tableLoading={configDataLoading || auditLogsLoading}
          hasMoreData={hasMoreData}
          fetchMoreData={fetchMoreData}
        />
      </CustomScrollbar>
    </section>
  );
};

export default AuditLogs;
